import { FC } from 'react';
import styled from '@emotion/styled';
import {
  Button,
  Card,
  PhoneField,
  PhoneFieldResult,
  SearchField,
  SearchWithSuggestions,
  Spacing,
  StyledDiv,
  Typography,
} from '@taleemabad/dsm/components';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';

export interface SchoolRegistrationData {
  schoolName: string;
  emis: string;
  address: string;
  city: string;
  region: string;
  schoolGroup: string;
  numberOfStudents: string;
  numberOfTeachers: string;
  phoneNumber: string;
  principalName: string;
}

export type SchoolRegistrationField = keyof SchoolRegistrationData;

export interface SchoolRegistrationProps {
  schoolName?: string;
  emis?: string;
  address?: string;
  city?: string;
  region?: string;
  schoolGroup?: string;
  numberOfStudents?: string;
  numberOfTeachers?: string;
  phoneNumber?: string;
  principalName?: string;
  regions?: Array<{ id: string; label: string }>;
  schoolGroups?: Array<{ id: string; label: string }>;
  onChange?: (field: SchoolRegistrationField, value: string) => void;
  onSubmit?: () => void;
  isLoading?: boolean;
}

export const SchoolRegistration: FC<SchoolRegistrationProps> = ({
  schoolName = '',
  emis = '',
  address = '',
  city = '',
  region = '',
  schoolGroup = '',
  numberOfStudents = '',
  numberOfTeachers = '',
  phoneNumber = '',
  principalName = '',
  regions = [],
  schoolGroups = [],
  onChange,
  onSubmit,
  isLoading = false,
}) => {
  const handleFieldChange =
    (field: SchoolRegistrationField) => (value: string) => {
      onChange?.(field, value);
    };

  const handleSuggestionChange =
    (field: SchoolRegistrationField) =>
    (item: { id: string; label: string } | null) => {
      onChange?.(field, item?.label || '');
    };

  const handlePhoneChange = (result: PhoneFieldResult | null) => {
    onChange?.('phoneNumber', result?.value || '');
  };

  return (
    <StyledContainer>
      <Spacing marginTop="xl" marginBottom="xl">
        <StyledFormCard variant="filled">
          <StyledDiv flexDirection="column">
            <Typography variant="textHeadlineMedium">
              School Registration
            </Typography>
            <Spacing marginTop="lg" />

            <StyledFieldsGrid>
              <StyledFieldGroup>
                <Typography variant="textBodyMedium">School Name *</Typography>
                <Spacing marginTop="sm" />
                <SearchField
                  value={schoolName}
                  placeholder="Enter school name"
                  onChange={handleFieldChange('schoolName')}
                />
              </StyledFieldGroup>

              <StyledFieldGroup>
                <Typography variant="textBodyMedium">EMIS Code *</Typography>
                <Spacing marginTop="sm" />
                <SearchField
                  value={emis}
                  placeholder="Enter EMIS code"
                  onChange={handleFieldChange('emis')}
                />
              </StyledFieldGroup>

              <StyledFieldGroup>
                <Typography variant="textBodyMedium">Address</Typography>
                <Spacing marginTop="sm" />
                <SearchField
                  value={address}
                  placeholder="Enter school address"
                  onChange={handleFieldChange('address')}
                />
              </StyledFieldGroup>

              <StyledFieldGroup>
                <Typography variant="textBodyMedium">City</Typography>
                <Spacing marginTop="sm" />
                <SearchField
                  value={city}
                  placeholder="Enter city"
                  onChange={handleFieldChange('city')}
                />
              </StyledFieldGroup>

              <StyledFieldGroup>
                <Typography variant="textBodyMedium">School Region</Typography>
                <Spacing marginTop="sm" />
                <SearchWithSuggestions
                  value={region}
                  placeholder="Select school region"
                  suggestions={regions}
                  onChange={handleSuggestionChange('region')}
                />
              </StyledFieldGroup>

              <StyledFieldGroup>
                <Typography variant="textBodyMedium">School Group</Typography>
                <Spacing marginTop="sm" />
                <SearchWithSuggestions
                  value={schoolGroup}
                  placeholder="Select school group"
                  suggestions={schoolGroups}
                  onChange={handleSuggestionChange('schoolGroup')}
                />
              </StyledFieldGroup>

              <StyledFieldGroup>
                <Typography variant="textBodyMedium">Number of Students *</Typography>
                <Spacing marginTop="sm" />
                <SearchField
                  value={numberOfStudents}
                  placeholder="Enter number of students"
                  onChange={handleFieldChange('numberOfStudents')}
                  type="number"
                />
              </StyledFieldGroup>

              <StyledFieldGroup>
                <Typography variant="textBodyMedium">Number of Teachers *</Typography>
                <Spacing marginTop="sm" />
                <SearchField
                  value={numberOfTeachers}
                  placeholder="Enter number of teachers"
                  onChange={handleFieldChange('numberOfTeachers')}
                  type="number"
                />
              </StyledFieldGroup>

              <StyledFieldGroup>
                <Typography variant="textBodyMedium">Phone Number *</Typography>
                <Spacing marginTop="sm" />
                <PhoneField
                  value={phoneNumber}
                  placeholder="Enter phone number"
                  onChange={handlePhoneChange}
                />
              </StyledFieldGroup>

              <StyledFieldGroup>
                <Typography variant="textBodyMedium">Principal Name</Typography>
                <Spacing marginTop="sm" />
                <SearchField
                  value={principalName}
                  placeholder="Enter principal name"
                  onChange={handleFieldChange('principalName')}
                />
              </StyledFieldGroup>
            </StyledFieldsGrid>

            <Spacing marginTop="xxxl" />
            <StyledButtonWrapper>
              <Button variant="filled" onClick={onSubmit} disabled={isLoading}>
                {isLoading ? 'Submitting...' : 'Submit Registration'}
              </Button>
            </StyledButtonWrapper>
          </StyledDiv>
        </StyledFormCard>
      </Spacing>
    </StyledContainer>
  );
};

// Styled Components
const StyledContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 var(--rk-spacing-lg);
`;

const StyledFormCard = styled(Card)`
  width: 100%;
  max-width: 600px;
  padding: var(--rk-spacing-xl);
  margin: 0 auto;

  ${mq(BreakpointSizes.desktop)} {
    max-width: 800px;
    padding: var(--rk-spacing-xxl);
  }
`;

const StyledFieldsGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--rk-spacing-lg);

  ${mq(BreakpointSizes.desktop)} {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--rk-spacing-xl);
  }
`;

const StyledFieldGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const StyledButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;

  ${mq(BreakpointSizes.mobile)} {
    justify-content: center;
  }
`;
